import { getBrowserDetails, Os } from './browser-details.js';
import { isNonEmptyString } from './typeguards.js';
import { getViewportDimensions } from './viewport-dimensions.js';

/**
 * An selector for document.querySelector(selector)
 */
export type Selector = string;

/**
 * Validates if a given Selector is useable by document.querySelector(selector)
 *
 * @param selector a given Selector
 * @returns true if selector is syntactically correct
 */
export const isSelectorValid = (selector: unknown): selector is Selector => {
    try {
        return isNonEmptyString(selector) && (!!document.createDocumentFragment().querySelector(selector) || true);
    } catch (err) {
        return false;
    }
};

export enum VisibilityMode { FULL, PARTIAL }

/**
 * checks visibility of element in elements window.
 * Uses getClientRects(), which will impact performance if called repeatedly. Use with care.
 *
 * @param element element to check
 * @param mode FULL or PARTIAL check
 * @returns true if visible
 */
export const isElementVisible = (element: HTMLElement, mode: VisibilityMode): boolean => {
    const win: Window | null = element.ownerDocument?.defaultView;
    const rect: DOMRectList = element.getClientRects();
    if (!win || rect.length === 0) return false;
    return isRectangleVisible(rect[0], mode, win);
};

/**
 * Checks visibility of rectangle based on predefined DOMRect against the window.
 * Use if you already have a valid DOMRect and don't want to pay performance to call costly getBoundingClientRect() or getClientRects() again.
 *
 * @param rect the rectangle to check
 * @param mode FULL or PARTIAL check
 * @param win (optional) the window to use - defaults to current window
 * @returns true if visible
 */
export const isRectangleVisible = (rect: DOMRectReadOnly, mode: VisibilityMode, win: Window = window): boolean => {
    // if the element is not in the DOM, we do not get a DOMRect and it is not visible
    if (!rect) return false;
    // destructing properties of the DOMRect
    const { top, left, right, bottom, width, height } = rect;
    // if all values are 0 using getBoundingClientRect, the element or a parent element is hidden
    if ([top, left, right, bottom, width, height].every(v => v === 0)) return false;
    // calculate the viewport dimensions of the given window
    const { viewportWidth, viewportHeight } = getViewportDimensions(win);
    // PARTIAL MODE: check if the element is partially visible by checking if at least one corner is within the viewport
    if (mode === VisibilityMode.PARTIAL) {
        return (
            Math.round(top) <= viewportHeight &&
            Math.round(left) <= viewportWidth &&
            Math.round(bottom) >= 0 &&
            Math.round(right) >= 0
        );
    }
    // FULL MODE: check if the element is fully visible by checking if the top, left, bottom and right are within the viewport
    return (
        Math.round(top) >= 0 &&
        Math.round(left) >= 0 &&
        Math.round(bottom) <= viewportHeight &&
        Math.round(right) <= viewportWidth
    );
};

/**
 * a helper HTMLElement to measure the top left corner of the viewport
 */
let offsetBase: HTMLElement | undefined;

/**
 * The `getBoundingClientRect()` method returns a DOMRect object providing information
 * about the size of an element and its position relative to the viewport.
 *
 * This implementation fixes some issues with the native ipad implementation.
 *
 * @param element HTMLElement to measure
 * @returns a DOMRect with size and position information
 */
export const getBoundingClientRect = (element: HTMLElement): DOMRect => {
    const boundingRect = element.getBoundingClientRect();
    const { os } = getBrowserDetails();

    if (os === Os.iOS) {
        let left = 0, top = 0, width = 0, height = 0;

        if (!offsetBase && document.body) {
            offsetBase = document.createElement('div');
            offsetBase.style.cssText = 'position:absolute;left:0;top:0';
            document.body.appendChild(offsetBase);
        }

        if (offsetBase && element.ownerDocument === document) {
            const baseRect = offsetBase.getBoundingClientRect();
            left = boundingRect.left - baseRect.left;
            top = boundingRect.top - baseRect.top;
            width = boundingRect.right - boundingRect.left;
            height = boundingRect.bottom - boundingRect.top;
        }

        const rect = {
            bottom: top + height,
            height,
            left,
            right: left + width,
            top,
            width,
            x: left,
            y: top,
        };

        return {
            ...rect,
            toJSON: () => JSON.stringify(rect),
        };
    }

    return boundingRect;
};
